import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { FloatingPortal, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { controlStyles } from './styles';

export type MediaLightboxProps = {
  show: boolean;
  onClose: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
};

export const MediaLightbox = ({ show, onClose, header, footer, children }: MediaLightboxProps) => {
  const { refs, context } = useFloating({
    open: show,
    onOpenChange: (open) => {
      if (!open) onClose();
    },
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });

  const { getFloatingProps } = useInteractions([dismiss, role]);

  if (!show) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        css={[
          (t) => css`
            height: calc(100dvh - ${t.heightOffset}px);
            position: fixed;
            top: ${t.heightOffset}px;
            left: 0;
            width: 100dvw;
            background: #00215280;
            display: grid;
            grid-template-rows: 44px 1fr auto;
            gap: ${t.spacing(4)};
            /* Above theme.zIndex.modals so responsive panels/modals (which jump to that z-index on narrow viewports) don't cover the lightbox. */
            z-index: ${t.zIndex.modals + 1};
            backdrop-filter: blur(${t.spacing(0.5)});
            padding: ${t.spacing(0, 5, 6, 5)};
          `,
          {
            [`@media (max-width: ${breakpoints.small.min}px)`]: {
              padding: theme.spacing(0, 1.5, 6, 1.5),
            },
          },
        ]}
        {...getFloatingProps()}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1.5),
            paddingBottom: 0,
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>{header}</div>
          <div
            css={[
              controlStyles,
              css`
                padding: 3px;
              `,
            ]}
          >
            <Button
              iconName='x'
              onClick={onClose}
              color='white'
              css={css`
                background: none;
                height: 24px;
                width: 24px;
                outline: none;
                &:hover {
                  background: none;
                }
              `}
            />
          </div>
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
            overflow: 'hidden',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {footer}
          </div>
        )}
      </div>
    </FloatingPortal>
  );
};
